import type { ReactNode } from "react";

import type { TSectionInitialData } from "@/api/dashboard/dashboard.types";
import {
  DEFAULT_SECTION_1,
  DEFAULT_SECTION_2,
  DEFAULT_SECTION_3,
  DEFAULT_SECTION_4,
  DEFAULT_SECTION_5,
  DEFAULT_SECTION_6,
  DEFAULT_SECTION_7,
} from "@/constants/sections";
import { PageHeader, PageLayout } from "../layout";
import type { TDashboardFeatureConfig } from "./BaseSectionDashboard";
import { DashboardSectionLayout } from "./DashboardSectionLayout";
import { CategoriesFeature } from "./features/CategoriesFeature";
import { ChatFeature } from "./features/ChatFeature";
import { CompanyKnowledgeFeature } from "./features/CompanyKnowledgeFeature";
import type { TDealNetworkRecommendationFilter } from "./features/dealNetworkFilters";
import { DealPipelineFeature } from "./features/DealPipelineFeature";
import { GeneralDashboardFeature } from "./features/GeneralDashboardFeature";
import { InvestorDueDiligenceFeature } from "./features/InvestorDueDiligenceFeature";
import {
  CollapsibleSection,
  CollapsibleSectionsProvider,
  ExpandCollapseAllButton,
} from "./CollapsibleDashboardSection";
import { InvestorReadinessFeature } from "./features/InvestorReadinessFeature";
import { InvestorReadinessTodosFeature } from "./features/InvestorReadinessTodosFeature";
import { MyDealNetworkFeature } from "./features/MyDealNetworkFeature";
import { MyDealStackFeature } from "./features/MyDealStackFeature";

export type TSectionDashboardContext = {
  sectionId: string | null;
  sectionName: string;
  description?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  dashboardData: TSectionInitialData | null;
  isLoading: boolean;
  error: string | null;
  enabledFeatures: TDashboardFeatureConfig;
  hasCompanyKnowledge: boolean;
  hasCategoriesTools: boolean;
  hasChat: boolean;
  categoriesCommonProps: {
    dashboardData: TSectionInitialData | null;
    isLoading: boolean;
    error: string | null;
    companyId: string | null;
  };
  dealNetworkFilter: TDealNetworkRecommendationFilter;
  dealNetworkFilterButtonId: string | null;
  handleDealNetworkCategorySelection: (selection: { selectedCategoryIds: string[]; isAllSelected: boolean }) => void;
  handleDealNetworkFilterButtonChange: (filterId: string | null) => void;
  readinessCategoryFilter: { selectedCategoryIds: string[]; isAllSelected: boolean };
  handleReadinessCategorySelection: (selection: { selectedCategoryIds: string[]; isAllSelected: boolean }) => void;
  readinessTodoRefreshCounter: number;
  handleReadinessTodoRefresh: () => void;
};

export type TSectionRenderer = (ctx: TSectionDashboardContext) => ReactNode;

const renderSection1: TSectionRenderer = (ctx) => (
  <PageLayout>
    <div className="min-h-0 flex-1">
      <GeneralDashboardFeature
        sectionId={ctx.sectionId}
        sectionName={ctx.sectionName}
        description={ctx.description}
        companyId={ctx.companyId ?? undefined}
        companyName={ctx.companyName ?? null}
      />
    </div>
  </PageLayout>
);

const renderSection2: TSectionRenderer = (ctx) => {
  const showInvestorReadinessTodos = ctx.enabledFeatures.showInvestorReadinessTodos ?? false;
  return (
    <PageLayout>
      {/* Fixed category filter bar — stays put while the content below scrolls */}
      {ctx.hasCategoriesTools && (
        <div className="flex-shrink-0">
          <CategoriesFeature
            {...ctx.categoriesCommonProps}
            fullWidth
            hideEmptyState
            onCategorySelectionChange={ctx.handleReadinessCategorySelection}
          />
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        <CollapsibleSectionsProvider sectionIds={["category-scoring", "funding-type-analysis", "todo"]}>
          <div className="flex flex-col gap-4">
            <PageHeader
              title={ctx.sectionName}
              subtitle={ctx.description ?? undefined}
              actions={<ExpandCollapseAllButton />}
            />
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="min-w-0 flex-1 lg:flex-[0_0_40%]">
                <InvestorReadinessFeature
                  companyId={ctx.companyId ?? undefined}
                  companyName={ctx.companyName ?? null}
                  sectionId={ctx.sectionId ?? undefined}
                  onRequestTodoRefresh={ctx.handleReadinessTodoRefresh}
                />
              </div>
              <div className="min-w-0 flex-1 space-y-4 lg:flex-[1_1_60%]">
                {showInvestorReadinessTodos && (
                  <CollapsibleSection
                    id="todo"
                    title="To-do tasks"
                  >
                    <InvestorReadinessTodosFeature
                      companyId={ctx.companyId ?? undefined}
                      externalRefreshCounter={ctx.readinessTodoRefreshCounter}
                      onRequestTodoRefresh={ctx.handleReadinessTodoRefresh}
                      categoryFilter={ctx.readinessCategoryFilter}
                    />
                  </CollapsibleSection>
                )}
              </div>
            </div>
          </div>
        </CollapsibleSectionsProvider>
      </div>
    </PageLayout>
  );
};

const renderSection4: TSectionRenderer = (ctx) => (
  <PageLayout>
    {/* Fixed category filter bar — stays put while the content below scrolls */}
    {ctx.hasCategoriesTools && (
      <div className="flex-shrink-0">
        <CategoriesFeature
          {...ctx.categoriesCommonProps}
          hideEmptyState
          hideCategoryLabel
          onCategorySelectionChange={ctx.handleDealNetworkCategorySelection}
          filterButtons={[
            { id: "shortlisted", label: "My Shortlist" },
            { id: "dismissed", label: "Dismissed" },
          ]}
          activeFilterButtonId={ctx.dealNetworkFilterButtonId}
          onFilterButtonChange={ctx.handleDealNetworkFilterButtonChange}
        />
      </div>
    )}
    <div className="min-h-0 flex-1 overflow-auto">
      <div className="flex flex-col gap-4">
        <PageHeader
          title={ctx.sectionName}
          subtitle={ctx.description ?? undefined}
        />
        <MyDealNetworkFeature
          companyId={ctx.companyId ?? undefined}
          sectionId={ctx.sectionId ?? undefined}
          companyName={ctx.companyName ?? null}
          recommendationFilter={ctx.dealNetworkFilter}
        />
      </div>
    </div>
  </PageLayout>
);

const renderSection5: TSectionRenderer = (ctx) => (
  <PageLayout>
    <MyDealStackFeature
      companyId={ctx.companyId ?? null}
      sectionId={ctx.sectionId ?? null}
      dashboardData={ctx.dashboardData}
      isLoading={ctx.isLoading}
      error={ctx.error}
    />
  </PageLayout>
);

const renderSection6: TSectionRenderer = (ctx) => (
  <PageLayout>
    <DealPipelineFeature
      dashboardData={ctx.dashboardData}
      isLoading={ctx.isLoading}
      error={ctx.error}
      companyId={ctx.companyId ?? null}
      sectionId={ctx.sectionId ?? null}
      title={ctx.sectionName}
      description={ctx.description}
    />
  </PageLayout>
);

const renderSection7: TSectionRenderer = (ctx) => (
  <PageLayout>
    <InvestorDueDiligenceFeature
      companyId={ctx.companyId ?? undefined}
      title={ctx.sectionName}
      description={ctx.description}
    />
  </PageLayout>
);

/** Hybrid renderer for unknown sections or feature-flag-based layouts */
const renderHybrid: TSectionRenderer = (ctx) => {
  if (ctx.hasCompanyKnowledge && !ctx.hasCategoriesTools && !ctx.hasChat) {
    return (
      <DashboardSectionLayout
        title={ctx.sectionName}
        subtitle={ctx.description ?? undefined}
      >
        <CompanyKnowledgeFeature
          companyId={ctx.companyId ?? undefined}
          companyName={ctx.companyName ?? null}
        />
      </DashboardSectionLayout>
    );
  }
  if (ctx.hasCategoriesTools && !ctx.hasCompanyKnowledge && !ctx.hasChat) {
    return (
      <DashboardSectionLayout
        title={ctx.sectionName}
        subtitle={ctx.description ?? undefined}
      >
        <CategoriesFeature {...ctx.categoriesCommonProps} />
      </DashboardSectionLayout>
    );
  }
  return (
    <DashboardSectionLayout
      title={ctx.sectionName}
      subtitle={ctx.description ?? undefined}
    >
      {ctx.hasCompanyKnowledge && (
        <CompanyKnowledgeFeature
          companyId={ctx.companyId ?? undefined}
          companyName={ctx.companyName ?? null}
        />
      )}
      {ctx.hasCategoriesTools && <CategoriesFeature {...ctx.categoriesCommonProps} />}
      {ctx.hasChat && (
        <div>
          <ChatFeature
            sectionId={ctx.sectionId}
            companyId={ctx.companyId ?? undefined}
          />
        </div>
      )}
    </DashboardSectionLayout>
  );
};

const SECTION_RENDERERS: Record<string, TSectionRenderer> = {
  [DEFAULT_SECTION_1]: renderSection1,
  [DEFAULT_SECTION_2]: renderSection2,
  [DEFAULT_SECTION_3]: renderSection5,
  [DEFAULT_SECTION_4]: renderSection4,
  [DEFAULT_SECTION_5]: renderSection5,
  [DEFAULT_SECTION_6]: renderSection6,
  [DEFAULT_SECTION_7]: renderSection7,
};

export function getSectionRenderer(sectionId: string | null): TSectionRenderer {
  if (!sectionId) return renderHybrid;
  return SECTION_RENDERERS[sectionId] ?? renderHybrid;
}
